import { useState } from 'react';
import { Mail, ArrowRight, Check } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EmailCapture = ({ variant = 'inline' }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: name || undefined })
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success('You\'re on the list! We\'ll keep you updated.');
      } else {
        const error = await response.json();
        if (error.detail?.includes('already')) {
          toast.info('You\'re already on the waitlist!');
          setIsSubmitted(true);
        } else {
          toast.error(error.detail || 'Failed to join waitlist');
        }
      }
    } catch (error) {
      console.error('Error joining waitlist:', error);
      toast.error('Failed to join waitlist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div 
        className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl"
        data-testid="waitlist-success"
      >
        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <Check className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <p className="text-emerald-400 font-manrope font-medium">You're on the list!</p>
          <p className="text-zinc-500 font-manrope text-sm">We'll send you exclusive updates.</p>
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <form onSubmit={handleSubmit} className="flex gap-2" data-testid="email-capture-inline">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          className="flex-1 bg-[#111111] border-white/10 text-white placeholder:text-zinc-600"
          data-testid="waitlist-email-input"
          required
        />
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-[#D4AF37] text-[#0A0A0A] px-6 hover:bg-[#B8860B] disabled:opacity-50"
          data-testid="waitlist-submit-btn"
        >
          {isSubmitting ? '...' : <ArrowRight className="w-5 h-5" />}
        </Button>
      </form>
    );
  }

  // Full variant with name
  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="email-capture-full">
      <div>
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600"
        />
      </div>
      <div className="flex gap-2">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          className="flex-1 bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600"
          data-testid="waitlist-email-input"
          required
        />
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-[#D4AF37] text-[#0A0A0A] px-6 hover:bg-[#B8860B] disabled:opacity-50"
          data-testid="waitlist-submit-btn"
        >
          {isSubmitting ? 'Joining...' : 'Join Waitlist'}
        </Button>
      </div>
      <p className="text-zinc-600 font-manrope text-xs">
        Get early access to registration and exclusive retreat updates.
      </p>
    </form>
  );
};

// Standalone waitlist section
export const WaitlistSection = () => {
  return (
    <section 
      id="waitlist" 
      className="py-16 px-6 bg-[#111111]"
      data-testid="waitlist-section"
    >
      <div className="max-w-xl mx-auto text-center">
        <Mail className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
        <h3 className="font-cormorant text-2xl font-medium text-white mb-2">
          Stay Updated
        </h3>
        <p className="text-zinc-400 font-manrope text-sm mb-6">
          Join our waitlist for early access and exclusive updates about The Reset retreat.
        </p>
        <EmailCapture variant="full" />
      </div>
    </section>
  );
};
